import { Package, Layers, Scale, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { cn } from 'utils/classnames';
import { Button } from 'components/ui/Button';

export const PalletBuildMenuPage = () => {
  const { t } = useTranslation();
  const { setCurrentScreen } = useAppStore();
  const { cartonId, pallet_id } = usePalletBuildStore();

  const steps = [
    {
      id: 'manage-case',
      label: t('palletBuildMenu.manageCaseLabel'),
      description: t('palletBuildMenu.manageCaseDescription'),
      icon: Package,
      screen: SCREEN_TYPES.PALLET_BUILD_CASE_SCAN,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      id: 'build-pallet',
      label: t('palletBuildMenu.buildPalletLabel'),
      description: t('palletBuildMenu.buildPalletDescription'),
      icon: Layers,
      screen: SCREEN_TYPES.PALLET_BUILD_PALLET_SCAN,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    {
      id: 'weigh-close',
      label: t('palletBuildMenu.weighCloseLabel'),
      description: t('palletBuildMenu.weighCloseDescription'),
      icon: Scale,
      screen: SCREEN_TYPES.PALLET_BUILD_WEIGH_CLOSE,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
  ];

  return (
    <div className="flex flex-col gap-4 py-4">
      <div className="mb-2 px-1">
        <span className="text-xs font-semibold uppercase tracking-widest text-emerald-500">
          {t('palletBuildMenu.eyebrow')}
        </span>
        <h1 className="text-2xl font-bold text-slate-800">{t('palletBuildMenu.heading')}</h1>
        <p className="text-sm text-left text-slate-400">{t('palletBuildMenu.subheading')}</p>
      </div>

      {(cartonId || pallet_id) && (
        <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            {cartonId && (
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span className="text-xs font-semibold text-slate-500">
                  {t('palletBuildMenu.caseLabel')}
                </span>
                <span className="font-mono text-xs font-bold text-slate-700 truncate">
                  {cartonId}
                </span>
              </div>
            )}
            {pallet_id && (
              <div className="flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span className="text-xs font-semibold text-slate-500">
                  {t('palletBuildMenu.palletLabel')}
                </span>
                <span className="font-mono text-xs font-bold text-slate-700 truncate">
                  {pallet_id}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {steps.map(({ id, label, description, icon: Icon, screen, iconBg, iconColor }, index) => (
          <button
            key={id}
            onClick={() => setCurrentScreen(screen as any)}
            className="flex w-full items-center gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition-all hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold">
              {index + 1}
            </div>
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                iconBg
              )}
            >
              <Icon className={cn('h-5 w-5', iconColor)} />
            </div>
            <div className="flex flex-1 flex-col items-start gap-0.5">
              <span className="text-base font-bold text-slate-800">{label}</span>
              <span className="text-xs text-slate-400">{description}</span>
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />
          </button>
        ))}
      </div>

      <div className="buttons-container !bg-transparent">
        <Button
          onClick={() => setCurrentScreen(SCREEN_TYPES.CALLBACK)}
          variant="secondary"
          className="flex-1 h-12"
        >
          {t('palletBuildMenu.backToMenu')}
        </Button>
      </div>
    </div>
  );
};
